import React, { useEffect, useMemo, useState } from "react";
import DataTable from "../../Pages/DataTable";
import { facultyAPI, courseFacultyAPI } from "../../Pages/api";

export default function FacultyViewer({
  name = "",
  email = "",
  activeStatus = "",
  funcSubAreaCode = "",
  autoLoad = true,
  toolbar = null,
  selectable = false,
  onSelect = () => {},
  alreadySelected = [],
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(autoLoad);
  const [error, setError] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const params = useMemo(() => {
    const p = { active_status: '1' };
    if (name.trim()) p.name = name.trim();
    if (email.trim()) p.email = email.trim();
    return p;
  }, [name, email, activeStatus]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await facultyAPI.getAll(params);
      let faculty = Array.isArray(data) ? data : [];
      if (funcSubAreaCode.trim()) {
        const mapped = await courseFacultyAPI.getAll({ func_sub_area_code: funcSubAreaCode.trim() });
        const allowedCodes = new Set((Array.isArray(mapped) ? mapped : []).map(m => String(m.faculty_code)));
        faculty = faculty.filter(f => allowedCodes.has(String(f.faculty_code)));
      }
      setRows(faculty);
      setCurrentPage(1);
      setSelectedIdx(null);
    } catch (e) {
      setRows([]);
      setError("Failed to load faculty data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoLoad) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, name, email, activeStatus, funcSubAreaCode]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const startIdx = (currentPage - 1) * pageSize;
  const pagedRows = rows.slice(startIdx, startIdx + pageSize);

  const handleRadioSelect = (pageRelativeIdx) => {
    const absoluteIdx = startIdx + pageRelativeIdx;
    setSelectedIdx(absoluteIdx);
  };

  const handleSubmit = () => {
    if (selectedIdx === null) return;
    onSelect(rows[selectedIdx]);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const columns = [
    ...(selectable
      ? [
          {
            header: "",
            className: "dt-col-narrow",
            renderCell: (_val, row, pageRelativeIdx) => {
              const alreadyAdded = alreadySelected.some(
                (f) => String(f.faculty_code) === String(row.faculty_code)
              );
              return (
                <input
                  type="radio"
                  name="facultySelect"
                  checked={selectedIdx === startIdx + pageRelativeIdx}
                  onChange={() => handleRadioSelect(pageRelativeIdx)}
                  disabled={alreadyAdded}
                  title={alreadyAdded ? "Already added" : ""}
                />
              );
            },
          },
        ]
      : []),
    { header: "Name", accessor: (row) => row.faculty_name ?? "", className: "dt-col-md" },
    { header: "Type", accessor: (row) => row.faculty_type ?? "", className: "dt-col-sm" },
    { header: "Email ID", accessor: (row) => row.email ?? "", className: "dt-col-lg" },
  ];

  const paginationStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    margin: "10px 0 0",
    fontFamily: "GEG-Body, sans-serif",
    fontSize: 12,
  };

  const pgBtnStyle = {
    padding: "3px 10px",
    border: "1px solid #ccc",
    borderRadius: 4,
    background: "#f7f7f7",
    cursor: "pointer",
    fontFamily: "GEG-Body, sans-serif",
    fontSize: 12,
  };

  return (
    <div style={{ padding: 16 }}>
      {loading && (
        <p style={{ fontFamily: "GEG-Body, sans-serif", fontSize: 12.5, color: "#666", marginTop: 10 }}>
          Loading faculty…
        </p>
      )}

      {error && (
        <p style={{ fontFamily: "GEG-Body, sans-serif", fontSize: 12.5, color: "red", marginTop: 10 }}>
          {error}
        </p>
      )}

      {!loading && (
        <>
          <DataTable
            columns={columns}
            data={pagedRows}
            showEditCols={false}
            editRows={{}}
            onToggleEditRow={() => {}}
            onChangeEditCell={() => {}}
            selectedForDelete={{}}
            onToggleDeleteRow={() => {}}
            emptyMessage="No faculty records found"
            rowKey={(row, i) => row.email || `${row.faculty_name}-${startIdx + i}`}
          />

          {selectable && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={
                  selectedIdx === null ||
                  alreadySelected.some(
                    (f) => String(f.faculty_code) === String(rows[selectedIdx]?.faculty_code)
                  )
                }
                style={{
                  backgroundColor: '#810055',
                  color: '#fff',
                  border: 'none',
                  padding: '5px 22px',
                  borderRadius: '20px',
                  cursor: selectedIdx === null ? 'not-allowed' : 'pointer',
                  fontFamily: 'GEG-Body, sans-serif',
                  fontSize: '12.5px',
                  opacity: selectedIdx === null ? 0.6 : 1,
                }}
              >
                Submit
              </button>
            </div>
          )}

          {rows.length > 0 && (
            <div style={paginationStyle}>
              <span>Rows per page:</span>
              <select value={pageSize} onChange={handlePageSizeChange} style={{ fontFamily: "GEG-Body, sans-serif", fontSize: 12 }}>
                {[5, 10, 20, 50].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>

              <button type="button" style={pgBtnStyle} disabled={currentPage <= 1} onClick={() => setCurrentPage(1)}>
                &laquo;
              </button>
              <button type="button" style={pgBtnStyle} disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>
                &lsaquo; Prev
              </button>

              <span>Page {currentPage} of {totalPages}</span>

              <button type="button" style={pgBtnStyle} disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
                Next &rsaquo;
              </button>
              <button type="button" style={pgBtnStyle} disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}>
                &raquo;
              </button>

              <span style={{ marginLeft: 8, color: "#666" }}>({rows.length} total)</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
